<template>
  <div class="equipment-details" :class="{ 'equipment-details--modal': isInModalMode }">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <OmxPageHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        :pills="headerPills"
        :sticky="!isInModalMode"
    >
      <template #actions>
        <template v-if="isEditMode">
          <b-button @click="cancelClicked" icon-left="close-thick" tabindex="31">{{ $t('common.cancel') }}</b-button>
          <OmxPermissionButton :allowed="canSave" type="is-primary" @click="saveClicked" icon-left="content-save" tabindex="32">{{ $t('common.save') }}</OmxPermissionButton>
        </template>
        <template v-else>
          <b-button v-if="!isInModalMode" @click="backClicked" icon-left="arrow-left">{{ $t('common.back') }}</b-button>
          <OmxPermissionButton :allowed="canEdit" type="is-primary" @click="editClicked" icon-left="pencil">{{ $t('common.edit') }}</OmxPermissionButton>
          <OmxPermissionButton :allowed="canDelete" type="is-danger" @click="deleteClicked" icon-left="delete">{{ $t('common.delete') }}</OmxPermissionButton>
        </template>
      </template>
    </OmxPageHeader>

    <OmxCard class="equipment-details__card">
      <div class="columns">
        <div class="column is-half">
          <OmxField
              v-if="isEditMode"
              v-model="equipmentData.model"
              :label="$t('equipment-details-view.equipment-model-field')"
              :placeholder="$t('equipment-details-view.equipment-model-placeholder')"
              rules="required"
              required
              tabindex="11"
          />
          <OmxField
              v-else
              :label="$t('equipment-details-view.equipment-model-field')"
              :value="equipmentData.model"
              view-only
          />

          <OmxField
              v-model="equipmentData.serial_number"
              :label="$t('equipment-details-view.equipment-serialnumber-field')"
              :placeholder="$t('equipment-details-view.equipment-serialnumber-placeholder')"
              :view-only="!isEditMode"
              tabindex="12"
          />

          <OmxTagInput
              v-model="selectedCategories"
              :label="$t('equipment-details-view.equipment-category-field')"
              :data="filteredCategories"
              :loading="isFetchingCategories"
              :view-only="!isEditMode"
              :maxtags="1"
              :allow-new="true"
              :placeholder="$t('equipment-details-view.equipment-category-placeholder')"
              tabindex="13"
              @typing="getFilteredCategories"
              @input="onCategoriesInput"
              @blur="onCategoryInputBlur"
          />

          <OmxTagInput
              v-model="selectedProviders"
              :label="$t('equipment-details-view.equipment-provider-field')"
              :data="filteredProviders"
              :loading="isFetchingCategories"
              :view-only="!isEditMode"
              :maxtags="1"
              :allow-new="true"
              :placeholder="$t('equipment-details-view.equipment-provider-placeholder')"
              tabindex="14"
              @typing="getFilteredProviders"
              @input="onProvidersInput"
              @blur="onProviderInputBlur"
          />

          <OmxField
              v-model="equipmentData.text"
              type="textarea"
              :label="$t('equipment-details-view.equipment-text-field')"
              :placeholder="$t('equipment-details-view.equipment-text-placeholder')"
              :view-only="!isEditMode"
              tabindex="15"
          />
        </div>

        <div class="column">
          <OmxField
              v-if="isEditMode"
              v-model="equipmentData.meta.estimation.quantity"
              type="number"
              min="0"
              step="any"
              :label="$t('equipment-details-view.equipment-quantity-field')"
              :placeholder="$t('equipment-details-view.equipment-quantity-placeholder')"
              :hint="$t('enums.duration-unit.day')"
              tabindex="16"
          />
          <OmxField
              v-else
              :label="$t('equipment-details-view.equipment-quantity-field')"
              view-only
          ><template #value>{{ getFormattedQuantityEquipment(equipmentData.meta.estimation, equipmentData.unit) }}</template></OmxField>

          <OmxField
              v-if="isEditMode"
              v-model="equipmentData.meta.estimation.fee_per_unit"
              type="number"
              min="0"
              step="any"
              :label="$t('equipment-details-view.equipment-fee-per-unit-field')"
              :placeholder="$t('equipment-details-view.equipment-fee-per-unit-placeholder')"
              :hint="defaultValues.priceUnit"
              tabindex="18"
          />
          <OmxField
              v-else
              :label="$t('equipment-details-view.equipment-fee-per-unit-field')"
              view-only
          ><template #value>{{ getFormattedFeePerUnit(equipmentData.meta.estimation) }}</template></OmxField>

          <p v-if="isEditMode && invalidEstimation" class="equipment-details__validation">
            <b-icon icon="alert-circle-outline" size="is-small" />
            {{ $t('validation.equipment-details') }}
          </p>
        </div>
      </div>
    </OmxCard>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";
import OmxField from "@/components/OmxField.vue";
import OmxTagInput from "@/components/OmxTagInput.vue";

export default {
  name: "EquipmentDetails",
  components: {
    OmxPageHeader,
    OmxCard,
    OmxField,
    OmxTagInput
  },
  props: {
    'project_id': {
      type: String,
    },
    'equipment_id': {
      type: String,
    },
    'is_template': {
      type: Boolean,
    }
  },
  data() {
    return {
      equipmentData: {
        meta: {
          estimation: {
            quantity: null,
            fee_per_unit: null
          }
        },
        unit: ""
      },
      isLoading: false,
      isEditMode: false,
      invalidEstimation: false,
      isFetchingCategories: false,
      categoricalData: {},
      filteredCategories: [],
      selectedCategories: [],
      filteredProviders: [],
      selectedProviders: []
    }
  },
  methods: {
    readEquipmentData: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getEquipmentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getEquipmentFunction({
        action: "get_equipment_by_id",
        project_id: this.project_id,
        document_id: this.equipment_id,
        fill_meta: true
      }).then((result) => {
        this.isLoading = false;
        this.equipmentData = result.data["equipments"];
        if (!this.equipmentData.meta.estimation)
          this.$set(this.equipmentData.meta, 'estimation', {});
        const category = this.equipmentData.category
        if (category !== undefined && category !== "") {
          this.selectedCategories[0] = category
        }
        const provider = this.equipmentData.provider
        if (provider !== undefined && provider !== "") {
          this.selectedProviders[0] = provider
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting equipments:", error?.code);
      });
    },
    createEquipment: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertEquipmentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_equipment";
      insertEquipmentFunction(updateData).then(() => {
        this.isLoading = false
        this.showSuccess(this.$t('messages.equipment-created'));
        this.$emit('equipmentAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(this.$t('messages.save-error'))
        console.error("Error creating equipment:", error?.code);
      });
    },
    updateEquipment: function (updateData) {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const updateEquipmentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_equipment";
      updateEquipmentFunction(updateData).then(() => {
        this.isLoading = false;
        this.isEditMode = false;
        this.showSuccess(this.$t('messages.equipment-updated'));
      }).catch((error) => {
        this.isLoading = false;
        this.showError(this.$t('messages.save-error'))
        console.error("Error updating equipment:", error?.code);
      });
    },
    deleteEquipment: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteEquipmentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteEquipmentFunction({
        action: "delete_equipment",
        project_id: this.project_id,
        document_id: this.equipment_id,
      }).then(() => {
        this.isLoading = false;
        this.isEditMode = false;
        this.showSuccess(this.$t('messages.equipment-deleted'));
        this.$emit('equipmentDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting equipment:", error?.code);
      });
    },
    readCategoricalData: function () {
      this.isFetchingCategories = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getCategoriesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getCategoriesFunction({
        action: "get_categories",
        collections: ["equipments"]
      }).then((result) => {
        this.isFetchingCategories = false
        this.categoricalData = result.data["equipments"]
        this.filteredCategories = this.categoricalData.category
        this.filteredProviders = this.categoricalData.provider
      }).catch((error) => {
        this.isFetchingCategories = false
        this.showError(error.message)
        console.log("Error getting categories:", error?.code);
      });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    async deleteClicked() {
      const ok = await this.$omxConfirm({
        title: this.$t('equipment-details-view.delete-equipment-confirm-title').toString(),
        message: this.$t('equipment-details-view.delete-equipment-confirm-message').toString(),
        confirmText: this.$t('equipment-details-view.delete-equipment-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deleteEquipment();
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$emit('close');
      } else if (this.isEditMode) {
        this.readEquipmentData();
        this.isEditMode = false;
      }
    },
    backClicked: function () {
      this.$router.back()
    },
    saveClicked: function () {
      // Required fields must be present before we attempt to save.
      const model = (this.equipmentData.model || "").toString().trim();
      if (!model) {
        this.showError(this.$t('messages.required-fields-error'));
        return;
      }

      const dataFieldsToUpdate = {
        project_id: this.project_id,
        model: this.equipmentData.model,
        serial_number: this.equipmentData.serial_number,
        text: this.equipmentData.text,
        estimation: {}
      }
      if (this.selectedCategories[0] !== undefined) {
        dataFieldsToUpdate.category = this.selectedCategories[0]
      }
      if (this.selectedProviders[0] !== undefined) {
        dataFieldsToUpdate.provider = this.selectedProviders[0]
      }
      let estimation = this.equipmentData.meta.estimation;
      if (estimation.quantity == null || estimation.quantity === "" || estimation.quantity === undefined) {
        this.invalidEstimation = true;
      } else {
        dataFieldsToUpdate.estimation.quantity = parseFloat(this.equipmentData.meta.estimation.quantity);
        this.equipmentData.unit = this.defaultValues.equipmentQuantityUnitId;
        dataFieldsToUpdate.unit = this.equipmentData.unit;
      }
      if (estimation.fee_per_unit == null || estimation.fee_per_unit === "" || estimation.fee_per_unit === undefined) {
        this.invalidEstimation = true;
      } else {
        dataFieldsToUpdate.estimation.fee_per_unit = parseFloat(this.equipmentData.meta.estimation.fee_per_unit);
      }

      if (this.isInNewMode) {
        this.createEquipment(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate.document_id = this.equipment_id;
        this.updateEquipment(dataFieldsToUpdate);
      }
    },
    onCategoriesInput() {
      this.filteredCategories = this.categoricalData.category
    },
    getFilteredCategories(text) {
      this.filteredCategories = this.categoricalData.category.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onProvidersInput() {
      this.filteredProviders = this.categoricalData.provider
    },
    getFilteredProviders(text) {
      this.filteredProviders = this.categoricalData.provider.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onCategoryInputBlur() {
      this.$refs.categoryInput.addTag()
    },
    onProviderInputBlur() {
      this.$refs.providerInput.addTag()
    }
  },
  mounted() {
    if ((!this.is_template && !this.currentUserAccess("EquipmentDetails", this.UserActions.VIEW, this.project_id))
        || (this.is_template && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readCategoricalData()
    if (this.isInNewMode) {
      console.log("creating new equipment");
      this.isEditMode = true;
    } else {
      this.isEditMode = false;
      this.readEquipmentData();
    }
  },
  computed: {
    isInNewMode() {
      return this.equipment_id === "new";
    },
    isInModalMode() {
      return this.$route.name !== 'EquipmentDetails';
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('equipment-details-view.new-equipment-title')
      } else if (this.isEditMode) {
        return this.$t('equipment-details-view.edit-equipment-title') + " " + this.getAdjustedTitle(this.equipmentData.model)
      } else {
        return this.$t('equipment-details-view.view-equipment-title') + " " + this.getAdjustedTitle(this.equipmentData.model)
      }
    },
    headerTitle() {
      if (this.isInNewMode) return this.$t('equipment-details-view.new-equipment-title');
      return this.getAdjustedTitle(this.equipmentData.model);
    },
    headerSubtitle() {
      const parts = [];
      if (this.selectedCategories && this.selectedCategories[0]) parts.push(this.selectedCategories[0]);
      if (this.selectedProviders && this.selectedProviders[0]) parts.push(this.selectedProviders[0]);
      return parts.join(' · ') || null;
    },
    headerPills() {
      return this.equipmentData.serial_number
          ? [{ label: this.equipmentData.serial_number, tone: 'neutral', dot: false }]
          : [];
    },
    canEdit() {
      return !this.isEditMode
          && ((!this.is_template && this.currentUserAccess('EquipmentDetails', 'UPDATE', this.project_id))
              || (this.is_template && this.currentUserAccess('ProjectTemplates', 'UPDATE')));
    },
    canSave() {
      return (!this.is_template && this.currentUserAccess('EquipmentDetails', 'UPDATE', this.project_id))
          || (this.is_template && this.currentUserAccess('ProjectTemplates', 'UPDATE'));
    },
    canDelete() {
      return !this.isEditMode && !this.isInNewMode
          && ((!this.is_template && this.currentUserAccess('EquipmentDetails', 'DELETE', this.project_id))
              || (this.is_template && this.currentUserAccess('ProjectTemplates', 'DELETE')));
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.equipment-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.equipment-details--modal { padding: 4px; }
.equipment-details__card ::v-deep .columns:last-child { margin-bottom: 0; }
.equipment-details__validation {
  color: $omx-danger-600;
  font-size: 12.5px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
}
.estimation-value {
  max-width: 50%;
}

.required-field {
  color: red;
  font-size: 10pt;
}
</style>
